/**
 * LatestAlertsStrip — the newest still-open alerts, as the FIRST thing on a dashboard.
 *
 * Everything else the dashboard shows is a count: alerts today, people on site, cameras
 * online. Counts are the answer to "how is it going"; nobody opens a security dashboard to
 * read those first. They open it to find out whether something is happening RIGHT NOW — and
 * that question is answered by the alert's own picture, not by a number that includes it.
 * So the strip sits above the analytics sheet and shows the two newest open alerts as
 * pictures, with the count reduced to a pill beside the heading.
 *
 * It is a BAND, not a hero: one shallow row of small frames, sized so the analytics below it
 * stay on screen. Being first is what gives the alerts their weight here; being enormous only
 * pushes everything else down.
 *
 * THE PICTURE IS THE CARD. Severity, clock and the two actions ride on top of the snapshot
 * rather than beside it, and the name and place sit in a scrim along the bottom — the same
 * shape a camera view has, which is what an operator is used to reading. A card with no
 * image yet is the same size and the same dark surface with the alert's icon in the middle,
 * so a preview landing never reflows the row.
 *
 * AS MANY CARDS AS THE ROW HOLDS, and no scroll: the width is measured and the cards fill it
 * — two on a laptop, five on a wide monitor — rather than a fixed count that leaves half the
 * band empty on one screen and crops the news on another. Everything past what fits is one
 * dashed tile carrying the remainder, which opens the alert LIST, because a remainder is a
 * queue to work through. It stays a glance either way: the strip never wraps to a second row,
 * and `maxCards` stops a very wide screen from quietly becoming the list itself. When nothing
 * is open the row collapses to a single quiet line rather than empty frames — "clear" should
 * look calm, not broken.
 *
 * The two actions are the two an operator can take without reading anything else:
 * ACKNOWLEDGE ("I'm on it") and RESOLVE ("this is dealt with"). Both are optimistic — the
 * card leaves at once and the next open alert takes its place — and both are hidden when the
 * node doesn't implement them. Anything needing a note, an assignee or a judgement belongs on
 * the alert record, which the card itself opens.
 *
 * Preview sourcing matches AlertCard's, because the same alerts are drawn by both: a direct
 * `snapshotUrl` (local node) renders immediately; otherwise `loadPreview` resolves lazily
 * (cloud blob-fetches the node's image through the tunnel) and the card renders at once
 * without waiting for it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRightIcon,
  ArrowTrendingDownIcon,
  BellAlertIcon,
  CheckCircleIcon,
  CheckIcon,
  FireIcon,
  HandRaisedIcon,
  PhotoIcon,
  ShieldExclamationIcon,
  TruckIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';
import type { HexAlert } from '../../api/contracts';

/**
 * Severity, as a filled badge — the one place on this page where a solid block of alert
 * colour is right. The panel lists these by the dozen and tones them down for exactly that
 * reason; here there are two, over dark video, and the badge is competing with the picture
 * rather than with fifty siblings.
 */
const SEV: Record<string, { badge: string; ring: string; label: string }> = {
  critical: { badge: 'bg-red-600 text-white', ring: 'ring-red-500/60', label: 'Critical' },
  warning: { badge: 'bg-amber-500 text-amber-950', ring: 'ring-amber-400/50', label: 'Warning' },
  normal: { badge: 'bg-blue-600 text-white', ring: 'ring-blue-500/40', label: 'Info' },
};

/**
 * The badge's icon says WHAT KIND of thing tripped before the name is read — a person, a
 * vehicle, a fire — which is the distinction an operator triages on when two alerts share a
 * severity. Matched on the event type loosely (`person_detected`, `ppe-violation`,
 * `fire_smoke` are all the same family), falling back to the generic alert bell.
 */
const TYPE_ICONS: Array<[RegExp, typeof BellAlertIcon]> = [
  [/fire|smoke/, FireIcon],
  [/vehicle|anpr|plate|truck/, TruckIcon],
  [/ppe|helmet|vest/, ShieldExclamationIcon],
  [/theft|loiter/, HandRaisedIcon],
  [/fall/, ArrowTrendingDownIcon],
  [/face|person|people|intrusion|crowd|count/, UserIcon],
];

function typeIcon(alert: HexAlert): typeof BellAlertIcon {
  const key = `${alert.eventType || ''} ${alert.alertName || ''}`.toLowerCase();
  return TYPE_ICONS.find(([re]) => re.test(key))?.[1] || BellAlertIcon;
}

function fmtClock(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function timeAgo(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const s = Math.floor(Math.max(0, Date.now() - ms) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Where the alert happened, as the operator names the place: camera, then site. */
export interface AlertPlace {
  name?: string;
  site?: string;
}

export interface LatestAlertsStripProps {
  /** Active alerts, newest first. As many as fit the row are drawn — see `minCardWidth`. */
  alerts: HexAlert[];
  /** Total open on the node — drives the pill and the "+N more" tile, and is ≥ alerts.length. */
  open: number;
  /** No feed yet: placeholders, never the empty state. */
  loading?: boolean;
  /** Narrowest a card may be, in px. The row fits as many of these as it can and then lets
   *  them share out the remainder, so the strip is flush at any window width. */
  minCardWidth?: number;
  /** Ceiling on the cards a very wide screen may draw, however many would fit. Past about
   *  half a dozen the strip stops being a glance and becomes the alert list. */
  maxCards?: number;
  /** cameraId → where it is. Missing entries just render the alert without a place. */
  places?: Record<string, AlertPlace>;
  /** Lazy preview resolver for hosts whose snapshots aren't directly loadable (cloud). */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  /** Open one alert's record. */
  onOpenAlert?: (alert: HexAlert) => void;
  /** Omit to hide the action — a node that can't acknowledge shouldn't offer to. */
  onAcknowledge?: (alert: HexAlert) => void;
  onResolve?: (alert: HexAlert) => void;
  /** The live board (honeycomb / floor plan) — the heading's link. Omit if unreachable. */
  onOpenBoard?: () => void;
  /** The full alert LIST — where the "+N more" tile goes. The remainder is a queue to work
   *  through, and the queue is a list; the board answers "what is happening", which the
   *  cards already did. Omit and the tile becomes inert text. */
  onViewAll?: () => void;
  className?: string;
}

/**
 * Shared shape for every alert cell, so a card and a placeholder are interchangeable and the
 * row's height never depends on which of them is showing.
 *
 * The row fills the page: `flex-1` shares the width out between however many cards were
 * measured to fit (see `capacityFor`), so there is never a ragged gap at the right-hand end.
 * `max-w` is what stops that from re-inflating the cards on a quiet machine — with one alert
 * open, an unbounded `flex-1` would blow a single thumbnail up to the whole page, which is the
 * video-wall this strip was deliberately shrunk out of.
 *
 * Full width only on a phone, where the row stacks instead of sharing.
 */
const CELL = 'relative aspect-[16/9] w-full min-w-0 overflow-hidden rounded-xl sm:flex-1 sm:max-w-[22rem]';

/** `gap-3` between cells, in px — the capacity arithmetic has to agree with the class. */
const GAP = 12;
/** `sm:w-28` — the "+N more" tile, reserved out of the row when there is a remainder. */
const TILE_W = 112;

/**
 * How many cards fit across `width`, given the narrowest a card may be.
 *
 * Measured rather than left to `flex-wrap` because the tile beside them has to say how many
 * alerts are NOT shown: wrapping decides the count in the layout engine, where this component
 * can't read it, and "+3 more" beside four cards on one monitor and two on another is worse
 * than no number at all.
 */
function capacityFor(width: number, minCardWidth: number, reserveTile: boolean): number {
  const usable = width - (reserveTile ? TILE_W + GAP : 0);
  return Math.max(1, Math.floor((usable + GAP) / (minCardWidth + GAP)));
}

function AlertMedia({
  alert,
  loadPreview,
}: {
  alert: HexAlert;
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
}) {
  const directUrl = alert.snapshotUrl || null;
  // 0 until the node says the snapshot is stored. Alerts are broadcast before the storage
  // lane has written the image, so "no picture yet" is the normal first state, not a failure.
  const previewReady = alert.previewReady || 0;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Resolve lazily WITHOUT blocking the card, and revoke any object URL this created.
  useEffect(() => {
    setImgError(false);
    setResolvedUrl(null);
    if (directUrl || !loadPreview) return;
    let cancelled = false;
    let made: string | null = null;
    loadPreview(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setResolvedUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, directUrl, loadPreview, previewReady]);

  const src = imgError ? null : directUrl || resolvedUrl;
  const Icon = typeIcon(alert);

  return (
    <>
      {/* The dark ground is painted whether or not an image lands, so the scrim, the badge and
          the white type below are legible from the first frame and don't flip contrast when
          the picture arrives. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          {imgError ? (
            <PhotoIcon className="h-6 w-6 text-white/20" />
          ) : (
            <Icon className="h-6 w-6 animate-pulse text-white/25" />
          )}
        </span>
      )}
      {/* Bottom scrim only — a full overlay would grey the frame the operator is trying to
          read, and the top-row chips carry their own. */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
    </>
  );
}

export default function LatestAlertsStrip({
  alerts,
  open,
  loading = false,
  minCardWidth = 240,
  maxCards = 6,
  places,
  loadPreview,
  onOpenAlert,
  onAcknowledge,
  onResolve,
  onOpenBoard,
  onViewAll,
  className = '',
}: LatestAlertsStripProps) {
  // "2m ago" is a lie the moment it's painted. One re-render a minute keeps the whole row
  // honest for the cost of a timer, and there is no cheaper place to put it than here.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // How much room the row actually has. Measured on the SECTION, which is present in every
  // state — measuring the row itself would lose the observer each time the strip passed
  // through its empty state and come back at zero.
  const rowRef = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /**
   * As many cards as fit, capped.
   *
   * Two passes, because the "+N more" tile only exists if there IS a remainder and the
   * remainder depends on how many cards were drawn: fit the row first, and if that leaves
   * alerts over, fit it again with the tile's width taken out. Before the first measurement
   * a single card is assumed — one card that widens is a smaller jolt than four that vanish.
   */
  const capacity = useMemo(() => {
    if (!width) return 1;
    const loose = Math.min(capacityFor(width, minCardWidth, false), maxCards);
    if (open <= loose) return loose;
    return Math.min(capacityFor(width, minCardWidth, true), maxCards);
  }, [width, minCardWidth, maxCards, open]);

  const shown = alerts.slice(0, capacity);
  /** Nothing has arrived yet — placeholders, and no "+N more" until the count and the cards
   *  are talking about the same set (an `open` that lands before the feed would otherwise
   *  briefly claim every open alert is an extra one). */
  const skeleton = loading && shown.length === 0;
  const more = skeleton ? 0 : Math.max(0, open - shown.length);
  const empty = !loading && shown.length === 0;

  return (
    <section ref={rowRef} className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">Latest alerts</h2>
          {open > 0 && (
            <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide tabular-nums text-destructive">
              {open} open
            </span>
          )}
        </div>
        {onOpenBoard && (
          <button
            type="button"
            onClick={onOpenBoard}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted"
          >
            Alert board
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {empty ? (
        /* Deliberately one line, not two empty frames: an all-clear machine should read as
           calm. The board link above still gets the operator to the history. */
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-border bg-card px-4 py-3 text-[13px] text-muted-foreground">
          <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
          No open alerts — nothing needs attention right now.
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-stretch">
          {skeleton
            ? Array.from({ length: capacity }, (_, i) => (
                <div key={`skeleton-${i}`} className={`${CELL} animate-pulse bg-muted`} />
              ))
            : shown.map((alert) => (
                <AlertTile
                  key={alert.id || `${alert.cameraId}-${alert.timestamp}`}
                  alert={alert}
                  place={places?.[alert.cameraId]}
                  loadPreview={loadPreview}
                  onOpen={onOpenAlert}
                  onAcknowledge={onAcknowledge}
                  onResolve={onResolve}
                />
              ))}

          {more > 0 && (
            /* Not a card: the remainder has no single picture, and dressing it as one would
               claim there is one more alert worth looking at rather than a queue worth
               opening. Narrower than the cards for the same reason, and it opens the LIST —
               the remainder is work to get through, and that is what the list is for. */
            <button
              type="button"
              onClick={onViewAll}
              disabled={!onViewAll}
              /* No aspect of its own: in the row it simply stretches to whatever height the
                 cards set (align-self: stretch), and stacked on a phone it is as tall as its
                 own three lines rather than a second empty 16:9 frame. */
              className="group flex w-full shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border border-dashed border-border bg-card py-4 transition-colors hover:bg-muted disabled:cursor-default disabled:hover:bg-card sm:w-28 sm:py-0"
            >
              <span className="text-xl font-semibold tabular-nums text-foreground">+{more}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                more {more === 1 ? 'alert' : 'alerts'}
              </span>
              {onViewAll && (
                <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/10 text-destructive transition-colors group-hover:bg-destructive group-hover:text-white">
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </span>
              )}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function AlertTile({
  alert,
  place,
  loadPreview,
  onOpen,
  onAcknowledge,
  onResolve,
}: {
  alert: HexAlert;
  place?: AlertPlace;
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  onOpen?: (alert: HexAlert) => void;
  onAcknowledge?: (alert: HexAlert) => void;
  onResolve?: (alert: HexAlert) => void;
}) {
  const sev = SEV[alert.severity || 'normal'] || SEV.normal;
  const Icon = typeIcon(alert);
  const name = alert.alertName || alert.eventType || 'Alert';
  // Camera · site · age, with the blanks dropped rather than left as stray separators.
  const where = [place?.name, place?.site, timeAgo(alert.timestamp)].filter(Boolean).join(' • ');
  const actionable = !!alert.id;

  return (
    <div className={`${CELL} bg-neutral-900 ring-1 ${sev.ring} shadow-card`}>
      <AlertMedia alert={alert} loadPreview={loadPreview} />

      {/* The whole card opens the alert — a full-bleed button BEHIND the chips rather than a
          wrapper around them, because a button inside a button is invalid HTML and the
          browser resolves it by dropping one. The chips sit above this layer and stop the
          click themselves. */}
      {onOpen && (
        <button
          type="button"
          aria-label={`Open ${name}`}
          title="Open alert"
          className="absolute inset-0 z-0"
          onClick={() => onOpen(alert)}
        />
      )}

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-1.5 p-1.5">
        <span
          className={`pointer-events-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${sev.badge}`}
          title={`${sev.label} — ${(alert.eventType || '').replace(/[_-]+/g, ' ')}`.trim()}
        >
          <Icon className="h-3 w-3" />
          {sev.label}
        </span>

        <span className="flex items-center gap-1">
          {alert.timestamp != null && (
            <span className="rounded bg-black/60 px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums text-white">
              {fmtClock(alert.timestamp)}
            </span>
          )}
          {actionable && onAcknowledge && (
            <Tooltip label="Acknowledge — I'm on it" side="bottom" className="pointer-events-auto">
              <button
                type="button"
                aria-label="Acknowledge alert"
                onClick={() => onAcknowledge(alert)}
                className="pointer-events-auto rounded bg-black/60 p-1 text-white transition-colors hover:bg-emerald-600"
              >
                <CheckIcon className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
          {actionable && onResolve && (
            <Tooltip label="Resolve — dealt with" side="bottom" className="pointer-events-auto">
              <button
                type="button"
                aria-label="Resolve alert"
                onClick={() => onResolve(alert)}
                className="pointer-events-auto rounded bg-black/60 p-1 text-white transition-colors hover:bg-emerald-600"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
        </span>
      </div>

      {/* WHAT, then WHERE and WHEN — the order the alert is triaged in, and the reason the
          name gets the page's largest type below the heading. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-2">
        <p className="truncate text-[13px] font-semibold leading-tight text-white" title={name}>
          {name}
        </p>
        {where && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-white/75" title={where}>
            <VideoCameraIcon className="h-3 w-3 shrink-0" />
            <span className="truncate">{where}</span>
          </p>
        )}
      </div>
    </div>
  );
}
